import { useCallback, useState } from 'react';
import { message } from 'antd';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';

import exportToCsv from '~helpers/export-csv';
import { bookingJourneyType } from 'common/constants/booking';
import { formatPriceWithSpace } from '~helpers/currency';
import { dayFormatType, formatDay, moment } from '~helpers/day';
import { SecondaryStatus, SecondaryStatusLabel } from './list';

export default function useExportPayout() {
  const client = useApolloClient();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const handleExportCSV = useCallback(async queryParams => {
    try {
      setIsLoading(true);
      const queryResponse = await client.query(queryParams);

      const allListData =
        queryResponse?.data?.listPartnerPayoutRecords?.edges.map(edge => edge.node) || [];
      const csvData = allListData.map(item => {
        const csvColumns = [];
        [
          'partner.name',
          'partner.code',
          {
            key: 'amount',
            transfer: (record, item) => formatPriceWithSpace(record, item.currency),
          },
          {
            key: 'referrer',
            transfer: record => `${record?.firstName || ''} ${record?.lastName || ''}`,
          },
          'bookingReferenceId',
          'country.name',
          {
            key: 'stage',
            transfer: (text, record) => {
              let resultText = '-';

              if (text === 'BOOKING_COMPLETED') {
                resultText =
                  record.bookingJourney === bookingJourneyType.SEMI_AUTOMATIC
                    ? 'Order Completed'
                    : 'Opp Completed';
              } else if (text === 'BOOKING_PAID') {
                resultText = 'Landlord Paid';
              }

              return resultText;
            },
          },
          {
            key: 'updatedAt',
            transfer: record => `\t${formatDay(dayFormatType.UKWITHHM, record)}\t`,
          },
          {
            key: 'status',
            transfer: record =>
              record ? t(`cms.referral.payout.list.column.status.${record.toLowerCase()}`) : '-',
          },
          {
            key: 'secondaryStatus',
            transfer: (record, item) => {
              switch (record) {
                case SecondaryStatus.ManualCancel:
                  return `${t(SecondaryStatusLabel.ManualCancel)}: ${item.cancelReason}`;

                default:
                  return record
                    ? `${t(
                        SecondaryStatusLabel[
                          Object.keys(SecondaryStatusLabel)[
                            Object.values(SecondaryStatus).indexOf(record)
                          ]
                        ],
                      )}`
                    : '';
              }
            },
          },
          {
            key: 'createdAt',
            transfer: record => `\t${formatDay(dayFormatType.UKWITHHM, record)}\t`,
          },
        ].map(dataKey => {
          if (typeof dataKey === 'string') {
            csvColumns.push(get(item, dataKey) || '');
          } else {
            csvColumns.push(dataKey.transfer(get(item, dataKey.key), item));
          }
        });
        return csvColumns;
      });

      exportToCsv(`[Student.com][Payout]-${moment().format(dayFormatType.UKWITHHM)}.csv`, [
        [
          'Partner Name',
          'Partner Code',
          'Payout Amount',
          'Referral',
          'Opp/Order Number',
          'Country',
          'Trigger Point',
          'Update Time',
          'Status',
          'Cancel Reason',
          'Create Time',
        ],
        ...csvData,
      ]);
      setIsLoading(false);
    } catch (e) {
      message.error(e?.toString());
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { exportToCSV: handleExportCSV, isLoading };
}
